
import { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingSocialIcons from '../../components/base/FloatingSocialIcons';

const FaqsPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState('social-media');

  const categories = [
    { id: 'social-media', name: 'Social Media Marketing', icon: 'ri-share-line' },
    { id: 'website-design', name: 'Website Design', icon: 'ri-layout-line' },
    { id: 'seo', name: 'SEO Services', icon: 'ri-search-line' },
    { id: 'google-business', name: 'Google My Business', icon: 'ri-google-line' }
  ];

  const faqCategories = {
    'social-media': [
      {
        question: 'What makes Digital Experience Studio different from other social media agencies?',
        answer: 'We specialize exclusively in small businesses and understand your unique challenges. Unlike large agencies that treat you like a number, we provide personalized service, affordable pricing, and content that actually generates leads. Plus, our 100% satisfaction guarantee means you have nothing to lose.'
      },
      {
        question: 'How quickly will I see results from social media marketing?',
        answer: 'Most clients start seeing increased engagement within the first 2-4 weeks. Significant growth in followers and leads typically occurs within 2-3 months. However, social media is a long-term strategy, and the best results come from consistent, quality content over time.'
      },
      {
        question: 'Do I need to provide photos or content for social media?',
        answer: 'Not at all! We handle everything from photography to content creation. However, if you have specific photos or ideas you\'d like to include, we\'re happy to incorporate them into your content strategy.'
      },
      {
        question: 'Which social media platforms do you manage?',
        answer: 'We manage all major platforms including Instagram, Facebook, TikTok, LinkedIn, Twitter/X, Pinterest, and YouTube. We\'ll recommend the best platforms for your specific business and target audience during your free consultation.'
      },
      {
        question: 'Can I cancel my social media subscription anytime?',
        answer: 'Yes! We don\'t believe in locking you into long-term contracts. You can cancel your subscription at any time with 30 days notice. We\'re confident you\'ll love our services and want to stay with us.'
      },
      {
        question: 'Do you handle customer comments and messages on social media?',
        answer: 'Yes! Our Growth and Premium packages include community management. We\'ll respond to comments and messages on your behalf, maintaining your brand voice and ensuring timely engagement with your audience.'
      }
    ],
    'website-design': [
      {
        question: 'How long does it take to build a new website?',
        answer: 'Most websites are completed within 2-4 weeks, depending on complexity and the number of pages. Custom e-commerce sites may take 4-6 weeks. We provide a detailed timeline during your consultation based on your specific requirements.'
      },
      {
        question: 'Will my website be mobile-friendly and responsive?',
        answer: 'Absolutely! All our websites are built with mobile-first design principles. Your site will look and function perfectly on all devices - phones, tablets, and desktop computers. This is essential for both user experience and search engine rankings.'
      },
      {
        question: 'Can I update my website content myself after it\'s built?',
        answer: 'Yes! We build all websites with easy-to-use content management systems. We also provide training and documentation so you can make updates yourself. For ongoing support, we offer maintenance packages.'
      },
      {
        question: 'What happens if my website goes down or has technical issues?',
        answer: 'We provide ongoing technical support and monitoring. Our maintenance packages include regular updates, security monitoring, and quick issue resolution. We also offer emergency support for critical problems.'
      },
      {
        question: 'Do you provide website hosting and domain registration?',
        answer: 'Yes, we can handle your hosting and domain needs, or work with your existing providers. We recommend reliable hosting solutions that ensure fast loading times and excellent uptime for your business.'
      },
      {
        question: 'Will my website be optimized for search engines?',
        answer: 'Every website we build includes basic SEO optimization - proper page titles, meta descriptions, clean URLs, and fast loading times. For comprehensive SEO strategy, we offer dedicated SEO services to improve your search rankings.'
      }
    ],
    'seo': [
      {
        question: 'How long does SEO take to show results?',
        answer: 'SEO is a long-term strategy. You may start seeing some improvements in 3-4 months, but significant results typically take 6-12 months. The timeline depends on your industry competition, current website status, and the keywords you\'re targeting.'
      },
      {
        question: 'What\'s included in your SEO services?',
        answer: 'Our SEO services include keyword research, on-page optimization, technical SEO, content creation, link building, local SEO optimization, and monthly reporting. We create a custom strategy based on your business goals and target audience.'
      },
      {
        question: 'Do you guarantee first page Google rankings?',
        answer: 'We don\'t make ranking guarantees because Google\'s algorithm changes constantly. However, we do guarantee increased organic traffic, improved search visibility, and better user engagement through proven SEO strategies and best practices.'
      },
      {
        question: 'How do you measure SEO success?',
        answer: 'We track multiple metrics including organic traffic growth, keyword rankings, click-through rates, conversion rates, and local visibility. You\'ll receive detailed monthly reports showing progress and areas of improvement.'
      },
      {
        question: 'What\'s the difference between local SEO and regular SEO?',
        answer: 'Local SEO focuses on helping your business appear in local search results and Google Maps. It includes optimizing your Google My Business profile, local citations, and location-based keywords. Regular SEO targets broader, national keywords.'
      },
      {
        question: 'Can you help with SEO if my website is already built?',
        answer: 'Absolutely! We can optimize existing websites by improving page speed, fixing technical issues, optimizing content, and implementing SEO best practices. We\'ll conduct a comprehensive audit to identify opportunities for improvement.'
      }
    ],
    'google-business': [
      {
        question: 'Why is Google My Business important for my local business?',
        answer: 'Google My Business is crucial for local visibility. It helps you appear in local search results and Google Maps, displays important business information, allows customer reviews, and provides insights about how customers find your business online.'
      },
      {
        question: 'How do you optimize my Google My Business profile?',
        answer: 'We optimize your profile by completing all information fields, adding high-quality photos, selecting the right categories, optimizing your business description, managing customer reviews, and posting regular updates to keep your profile active.'
      },
      {
        question: 'How often should I post updates to my Google My Business?',
        answer: 'We recommend posting 2-3 times per week to keep your profile active and engaging. Posts can include business updates, promotions, events, new products/services, and behind-the-scenes content to connect with customers.'
      },
      {
        question: 'How do you handle negative reviews on Google My Business?',
        answer: 'We monitor your reviews daily and respond professionally to all feedback. For negative reviews, we craft thoughtful responses that address concerns, show you care about customer satisfaction, and demonstrate your commitment to service improvement.'
      },
      {
        question: 'Can you help get more positive Google reviews?',
        answer: 'Yes! We implement review generation strategies including follow-up emails, review request cards, and automated systems to encourage satisfied customers to leave positive reviews. We also provide guidance on best practices for requesting reviews.'
      },
      {
        question: 'What insights and analytics do you track for Google My Business?',
        answer: 'We track profile views, search queries that found your business, customer actions (calls, website visits, direction requests), photo views, and review metrics. This data helps us optimize your profile for better local visibility and customer engagement.'
      }
    ]
  };

  const currentFaqs = faqCategories[activeCategory] || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-50 relative overflow-hidden">
      <FloatingSocialIcons />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Got questions? We've got answers! Find everything you need to know about our digital marketing services.
            </p>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setActiveCategory(category.id);
                    setOpenIndex(0);
                  }}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-white/80 text-gray-700 hover:bg-white/90 hover:shadow-md'
                  }`}
                >
                  <i className={`${category.icon} text-lg`}></i>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {currentFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-white/50 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-bold text-gray-800 pr-8">{faq.question}</h3>
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-blue-600 transition-transform duration-300 ${
                        openIndex === index ? 'transform rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-8 pb-6 text-gray-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Links Section */}
        <section className="py-16 px-6 bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 bg-clip-text text-transparent">
              Still Have Questions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link
                to="/about"
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-information-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Learn About Us</h3>
                <p className="text-gray-600">Discover our story and mission</p>
              </Link>
              <Link
                to="/services"
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-full mx-auto mb-4">
                  <i className="ri-service-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">View Our Services</h3>
                <p className="text-gray-600">See what we can do for you</p>
              </Link>
              <Link
                to="/contact"
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 rounded-full mx-auto mb-4">
                  <i className="ri-mail-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800">Contact Us</h3>
                <p className="text-gray-600">Get in touch directly</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-400 rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Book your free consultation today and let's discuss how we can help your business grow through digital marketing!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="bg-white text-blue-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Free Consultation
              </Link>
              <Link
                to="/quote"
                className="bg-cyan-400 text-blue-800 px-8 py-4 rounded-full font-bold text-lg hover:bg-cyan-300 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FaqsPage;
